import { Chart } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import {
  AnimationEvent,
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  PointElement,
  ScriptableScaleContext,
  SubTitle,
  Title,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Options } from 'chartjs-plugin-datalabels/types/options';
import { chartValueFormatter, formatUnit } from 'PDF/helpers/chartFormatters';

import { calculatePadding, splitLabel } from 'helpers/charts';

import { emptyChart, IMixedChart } from '../types';

ChartJS.register(LinearScale, CategoryScale, BarElement, PointElement, Legend, BarController, Title, SubTitle);

export interface IMixedChartProps {
  handleOnComplete: (uri: string) => void;
  info: IMixedChart;
  aspectRatio: number;
  customDataLabels?: Options;
}

ChartJS.defaults.font.family = 'Noto Sans';

const labelMainProperties = {
  font: {
    size: 26,
    weight: 300,
  },
  color: '#77787C',
};

export const MixedChart = ({
  info: { labels, data, datasetsLabels, colors, source, unit = '' },
  aspectRatio,
  handleOnComplete,
  customDataLabels,
}: IMixedChartProps) => {

   const { t } = useTranslation();
  const allValues: string[] = [
    ...data.flatMap((dataset) => dataset.values.map((value) => Math.ceil(value).toString())),
  ];

  const chartData = {
    labels,
    datasets: data.map((item, index) => ({
      type: item.type,
      label: datasetsLabels[index],
      backgroundColor: colors[index],
      data: item.values,
      barPercentage: 1,
      categoryPercentage: data.length > 2 ? 0.7 : 0.5,
      ...(customDataLabels && {
        datalabels: {
          ...customDataLabels,
          color: colors[index],
        },
      }),
      borderColor: colors[index],
      borderWidth: 2,
      fill: false,
    })),
  };

  const options: ChartOptions = {
    aspectRatio,
    devicePixelRatio: 1,
    layout: {
      padding: {
        left: calculatePadding(allValues, unit),
      },
    },
    plugins: {
      title: {
        display: Boolean(unit),
        text: '',
        align: 'start',
        padding: { bottom: 40 },
        font: {
          size: 24,
          weight: 400,
        },
        color: labelMainProperties.color,
      },
      subtitle: {
        display: Boolean(source),
        text: `${t('source')}: ${source}`,
        position: 'bottom',
        align: 'start',
        ...labelMainProperties,
      },
      legend: {
        position: 'bottom',
        align: 'center',
        labels: {
          boxWidth: 26,
          padding: 30,
          usePointStyle: true,
          ...labelMainProperties,
          generateLabels: (chart) => {
            const data = chart?.data;
            return (
              data?.datasets?.map((el, i) => ({
                text: el.label as string,
                fontColor: labelMainProperties.color,
                fillStyle: colors[i],
                strokeStyle: colors[i],
                lineWidth: 4,
                pointStyle: el.type === 'bar' ? 'rect' : 'line',
                index: i,
              })) ?? []
            );
          },
        },
      },
      datalabels: {
        color: (ctx: any) => (ctx.dataset.type === 'bar' ? 'white' : ctx.dataset.borderColor),
        anchor: (ctx: any) =>
          ctx.dataset.type === 'bar' ? 'start' : ctx.dataset.data[ctx.dataIndex] > 0 ? 'end' : 'start',
        align: (ctx: any) =>
          ctx.dataset.type === 'bar' ? 'top' : ctx.dataset.data[ctx.dataIndex] > 0 ? 'end' : 'start',
        font: {
          weight: 400,
          size: 26,
        },
        formatter: chartValueFormatter,
      },
    },
    scales: {
      y: {
        display: true,
        ticks: {
          color: '#77787C',
          font: {
            size: 26,
            weight: 300,
          },
          padding: 10,
        },
        grid: {
          color: (ctx: ScriptableScaleContext) => (ctx.tick.value === 0 ? '#77787C' : 'transparent'),
          tickBorderDash: [3, 7],
        },
        border: { display: false, dash: [3, 7] },
      },
      x: {
        display: true,
        ticks: {
          ...labelMainProperties,
          font: {
            size: 26,
            weight: 300,
          },
          maxRotation: 0,
          padding: 20,
          callback: function (_: unknown, index: number) {
            return splitLabel(labels[index].toString());
          },
        },
        grid: {
          color: 'transparent',
        },
        border: { display: true, color: 'transparent' },
      },
    },
    animation: {
      duration: 0,
      onComplete(event: AnimationEvent) {
        handleOnComplete(
          !data.some((item) => item.values.some((value) => value !== 0)) ? emptyChart : event.chart.toBase64Image(),
        );
      },
    },
  };

  const plugins = [
    {
      id: 'unitPlugin',
      afterDraw(chart: ChartJS) {
        const ctx = chart.ctx;
        const leftX = chart.chartArea?.left;

        ctx.fillStyle = labelMainProperties.color;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.font = '300 24px Noto Sans';

        ctx.fillText(formatUnit(unit), leftX - 18, 20);
      },
    },
  ];

  return <Chart type="bar" data={chartData} options={options} plugins={[ChartDataLabels, ...(unit ? plugins : [])]} />;
};
